import { useEffect, useRef, useState } from "react";
import { Button, ErrorNote, MoneyField } from "../../components/ui";
import { formatMoney, parseMoney } from "../../lib/money";
import {
  loadStripe, createIntent, recordPayment, CARD_MAX_CENTS,
  type StripeLike,
} from "../../lib/stripe";

/**
 * Taking a card at the desk.
 *
 * The card field below is an iframe served by Stripe. The number is typed
 * into their page, not this one, and goes straight to them — this component
 * never sees it and neither does the database. That is what keeps the whole
 * system on PCI SAQ A.
 *
 * Charging is two steps on purpose. The worker creates the intent after
 * checking the cap and the deal's balance; Stripe then confirms the card; the
 * worker asks Stripe whether it really succeeded before writing the payment
 * row. A browser cannot talk its way into a payment that did not happen.
 */
export default function CardPayment({ dealId, owingCents, onTaken, onCancel }: {
  dealId: string;
  owingCents: number;
  onTaken: () => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [complete, setComplete] = useState(false);
  /* "stranded" is terminal. The card has been charged and the row has not
     been written; the one thing that must not happen next is a second
     charge, so the button does not come back. */
  const [stage, setStage] =
    useState<"idle" | "charging" | "recording" | "stranded">("idle");

  const stripeRef = useRef<StripeLike | null>(null);
  const cardRef = useRef<{ destroy(): void } | null>(null);
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    loadStripe()
      .then((stripe) => {
        if (!live || !mountRef.current) return;
        stripeRef.current = stripe;
        const elements = stripe.elements();
        const card = elements.create("card", {
          hidePostalCode: false,
          style: {
            base: {
              fontSize: "16px",          // under 16px, iOS zooms on focus
              color: "#141C24",
              fontFamily: "Inter, system-ui, sans-serif",
              "::placeholder": { color: "#68727D" },
            },
            invalid: { color: "#B3352B" },
          },
        });
        card.mount(mountRef.current);
        card.on("change", (e) => {
          setCardError(e.error?.message ?? null);
          setComplete(Boolean(e.complete));
        });
        cardRef.current = card;
        setReady(true);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Could not load Stripe."));
    return () => {
      live = false;
      cardRef.current?.destroy();
      cardRef.current = null;
    };
  }, []);

  const cents = parseMoney(amount);
  const ceiling = Math.min(CARD_MAX_CENTS, owingCents);
  const tooMuch = cents !== null && cents > ceiling;
  const canCharge = ready && complete && cents !== null && cents > 0
                    && !tooMuch && stage === "idle";

  async function charge() {
    const stripe = stripeRef.current;
    const card = cardRef.current;
    if (!stripe || !card || cents === null) return;

    setErr(null); setStage("charging");
    /* Tracked here rather than read off `stage` in the catch below. State
       read inside a catch is the value from the render that started this
       call, so `stage === "recording"` was always false and the warning that
       the card had been charged never appeared. */
    let moved = false;
    try {
      const { clientSecret } = await createIntent(dealId, cents);
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: card as unknown as object },
      });
      if (result.error) {
        // A declined card is the customer's bank talking, not a fault here.
        setErr(result.error.message);
        setStage("idle");
        return;
      }
      const intent = result.paymentIntent;
      if (!intent || intent.status !== "succeeded") {
        setErr(`The payment did not complete (${intent?.status ?? "unknown"}).`);
        setStage("idle");
        return;
      }

      // The money has moved. From here the row must be written, so a failure
      // says so loudly rather than losing a real payment quietly.
      moved = true;
      setStage("recording");
      await recordPayment(intent.id);
      onTaken();
    } catch (e) {
      setErr(
        (e instanceof Error ? e.message : "Something went wrong.")
        + (moved
            ? " The card WAS charged — do not take it again. Check the deal, "
              + "and Stripe, before retrying."
            : ""));
      setStage(moved ? "stranded" : "idle");
    }
  }

  return (
    <>
      <p className="taxnote">
        The card is typed into Stripe's own secure field below. The number does
        not pass through FrontLot and is never stored — only the brand and last
        four digits are kept with the payment.
      </p>

      <MoneyField label="Amount" value={amount} onChange={setAmount}
                  hint={`Up to ${formatMoney(ceiling)}`}
                  error={tooMuch
                    ? (owingCents < CARD_MAX_CENTS
                        ? `That is more than the deal owes (${formatMoney(owingCents)}).`
                        : `Cards are capped at ${formatMoney(CARD_MAX_CENTS)}. Take the `
                          + "balance by cashier's check, ACH or wire.")
                    : null} />

      <div className="field">
        <label>Card</label>
        <div className="cardfield" ref={mountRef} />
        {!ready && !err && <p className="hint dim">Loading the secure card field</p>}
        {cardError && <p className="hint err">{cardError}</p>}
      </div>

      {err && <ErrorNote>{err}</ErrorNote>}

      <div className="actions">
        <Button onClick={charge} disabled={!canCharge}>
          {stage === "charging" ? "Charging"
            : stage === "recording" ? "Recording"
            : stage === "stranded" ? "Charged — do not retry"
            : cents ? `Charge ${formatMoney(cents)}` : "Charge card"}
        </Button>
        <Button variant="ghost" onClick={onCancel}
                disabled={stage === "charging" || stage === "recording"}>
          {stage === "stranded" ? "Close" : "Cancel"}
        </Button>
      </div>

      <p className="hint dim">
        Keyed cards carry the highest chargeback risk of any method — no chip,
        no signature. The {formatMoney(CARD_MAX_CENTS)} ceiling is what bounds
        it, and it is enforced by the database as well as this form.
      </p>
    </>
  );
}
